<template>
  <div>
    <h3>Suppliers</h3>

    <div v-if="isLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="custom-search d-flex justify-content-between">
        <b-form-group>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
          />
        </b-form-group>
        <b-form-group>
          <b-button
            variant="primary"
            class="mx-1 float-right"
            size="sm"
            @click="openSupplierModal(null)"
          >
            <span>Add supplier</span>
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
        v-if="suppliersTableView"
        :columns="columns"
        :rows="filteredSuppliers"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'image'">
            <img
              :src="imageUrl(props.row.image)"
              class="table-row-thumb"
              alt=""
            />
          </span>
          <span v-else-if="props.column.field === 'action'" class="action-link-group suppliers-action">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openSupplierModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>View / Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteSupplier(props.row)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <template #pagination-bottom="props">
          <TablePagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; props.perPageChanged({ currentPerPage: n }) }"
            />
        </template>
      </vue-good-table>
    </template>
    <ManageSupplier
      :click-date="clickDate"
      :supplier="supplier"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormInput,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import ManageSupplier from '@/views/Components/ManageSupplier.vue'
import { $themeConfig } from '@themeConfig'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  components: {
    TablePagination,
    ManageSupplier,
    DatatableCellValue,
    BButton,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BFormInput,
    BSpinner,
    BPagination,
    VueGoodTable,
  },
  data() {
    const { noUnitImage } = $themeConfig.app

    return {
      noUnitImage,
      supplier: null,
      clickDate: null,
      suppliersTableView: true,
      isLoading: true,
      searchTerm: '',
      columns: [
        {
          label: 'Image',
          field: 'image',
          thClass: 'table-picture-col',
          tdClass: 'table-picture-col',
          sortable: false,
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Phone',
          field: 'phone',
        },
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center suppliers-action-col',
          tdClass: 'text-center suppliers-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
    }
  },
  computed: {
    ...mapGetters({
      suppliers: 'suppliers/suppliers',
    }),
    filteredSuppliers() {
      const keyword = String(this.searchTerm || '').trim().toLowerCase()
      if (!keyword) {
        return this.suppliers
      }
      const fields = [
        'id',
        'name',
        'description',
        'email',
        'phone',
        'website',
        'manager_name',
        'manager_phone',
        'manager_email',
      ]
      return (this.suppliers || []).filter((supplier) => (
        fields.some((field) => String(supplier[field] ?? '').toLowerCase().includes(keyword))
      ))
    },
  },
  async mounted() {
    await this.loadSuppliers().catch(() => {
      toastFailure('Can not load suppliers')
    })
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      loadSuppliers: 'suppliers/loadSuppliers',
      deleteSupplierAction: 'suppliers/deleteSupplier',
    }),
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      })
    },
    async deleteSupplier(supplier) {
      const message = `Are you sure you want to delete "${supplier.name}"? This cannot be undone.`
      if (!await this.confirmationMessage(message)) {
        return
      }

      this.deleteSupplierAction(supplier.id)
        .then(() => {
          toastSuccess('Supplier has been deleted.')
        })
        .catch((error) => {
          const data = error?.response?.data
          const apiMessage = data?.error?.[0] || data?.message
          toastFailure(apiMessage || 'Failed to delete the supplier.')
        })
    },
    imageUrl(value) {
      if (!value || String(value).trim() === '') {
        return this.noUnitImage || NO_IMAGE
      }
      const p = String(value).trim()
      if (/^https?:\/\//i.test(p) || p.startsWith('//')) {
        return p
      }
      return p.startsWith('/') ? p : `//${p}`
    },
    openSupplierModal(row) {
      this.refreshSuppliers().finally(() => {
        this.supplier = row
        this.clickDate = new Date()
      })
    },
    refreshSuppliers() {
      return this.loadSuppliers().catch(() => {
        toastFailure('Can not load suppliers')
      })
    },
  },
}
</script>

<style scoped>
.suppliers-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.suppliers-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.suppliers-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
